import React, { useMemo } from "react";
import cn from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { useIntl } from "react-intl";
import { isFacebookMessagingConnected, isInstagramMessagingConnected } from "../../../cores/socialPost/config";
import { useFacebookCore } from "../../../cores/facebook";
import { useMeCore } from "../../../cores/me";
import { useRouterCore } from "../../../cores/router";
import { useSocialChatCore } from "../../../cores/socialChat";
import {
  WHATSAPP_COLOR,
  FACEBOOK_COLOR,
  INSTAGRAM_COLOR,
  LINKED_IN_COLOR,
  LINKED_IN_BUSINESS_COLOR,
} from "../../../constants/socialColors.constant";
import { Conversations } from "../../../cores/socialChat/interfaces/conversations-group.interface";
import { InputWembii } from "../InputWembii";
import { FilterOption } from "./filterOption/FilterOption";
import { useFilterDate } from "../../../hooks/useFilterDate/useFilterDate.hook";
import { Conversation } from "../../../cores/socialChat/interfaces/conversations.interface";
import { SocialType } from "../../../cores/socialChat/types/social.types";
import { ConversationStatus } from "../../../cores/socialChat/types/conversation-status.types";

export type WebChatListFilter = "all" | "unread" | "unresolved";

type Props = {
  conversations: Conversations;
  searchText: string;
  onSearchChange: (value: string) => void;
  listFilter: WebChatListFilter;
  onListFilterChange: (f: WebChatListFilter) => void;
  onOpenAdvancedFilters: () => void;
};

type ChannelConfig = {
  id: SocialType;
  labelId: string;
  icon: IconProp;
  brand: string;
  enabled: boolean;
};

export default function WebChatInboxToolbar({
  conversations,
  searchText,
  onSearchChange,
  listFilter,
  onListFilterChange,
  onOpenAdvancedFilters,
}: Props) {
  const intl = useIntl();
  const { goToBinding } = useRouterCore();
  const { socialSettings } = useMeCore();
  const { settings: facebookSettings } = useFacebookCore();
  const facebookMessagingConnected =
    isFacebookMessagingConnected(socialSettings) ||
    Boolean(facebookSettings?.token);
  const {
    conversationStatus,
    selectedSocial,
    setSelectedSocial,
    setConversationStatus,
    dateFilter,
  } = useSocialChatCore();
  const { filterDateConversation } = useFilterDate(dateFilter);

  const configuredSocials = {
    whatsapp: !!socialSettings?.whatsapp,
    instagram: isInstagramMessagingConnected(socialSettings),
    facebook: facebookMessagingConnected,
    linkedin: !!socialSettings?.linkedIn,
    linkedinBusiness: !!socialSettings?.linkedInBusiness,
  };

  const channels = useMemo<ChannelConfig[]>(
    () => [
      {
        id: "all",
        labelId: "app.chat.filterLabel",
        icon: ["fas", "comments"],
        brand: "#2563eb",
        enabled: true,
      },
      {
        id: "web",
        labelId: "app.chat.channel.web",
        icon: ["fas", "globe"],
        brand: "#0d4b7e",
        enabled: true,
      },
      {
        id: "whatsapp",
        labelId: "app.contacts.whatsapp",
        icon: ["fab", "whatsapp"],
        brand: WHATSAPP_COLOR,
        enabled: configuredSocials.whatsapp,
      },
      {
        id: "facebook",
        labelId: "app.sidebar.metrics.Facebook",
        icon: ["fab", "facebook"],
        brand: FACEBOOK_COLOR,
        enabled: configuredSocials.facebook,
      },
      {
        id: "instagram",
        labelId: "app.sidebar.metrics.Instagram",
        icon: ["fab", "instagram"],
        brand: INSTAGRAM_COLOR,
        enabled: configuredSocials.instagram,
      },
      {
        id: "linkedin",
        labelId: "app.chat.channel.linkedin",
        icon: ["fab", "linkedin"],
        brand: LINKED_IN_COLOR,
        enabled: configuredSocials.linkedin,
      },
      {
        id: "linkedinBusiness",
        labelId: "app.chat.channel.linkedinBusiness",
        icon: ["fab", "linkedin"],
        brand: LINKED_IN_BUSINESS_COLOR,
        enabled: configuredSocials.linkedinBusiness,
      },
    ],
    [configuredSocials]
  );

  const visibleChannels = useMemo(
    () => channels.filter((channel) => channel.enabled),
    [channels]
  );

  const hasMoreChannelsToConnect = useMemo(
    () =>
      !configuredSocials.whatsapp ||
      !configuredSocials.facebook ||
      !configuredSocials.instagram ||
      !configuredSocials.linkedin ||
      !configuredSocials.linkedinBusiness,
    [configuredSocials]
  );

  const bucketConversations = useMemo((): Conversation[] => {
    const bucket = conversations[selectedSocial] ?? {
      active: [],
      archived: [],
      inTrash: [],
    };
    return bucket[conversationStatus].filter(filterDateConversation);
  }, [
    conversations,
    selectedSocial,
    conversationStatus,
    filterDateConversation,
  ]);

  const hasUnread = useMemo(
    () => bucketConversations.some((c) => c.unreadCount > 0),
    [bucketConversations]
  );

  const statusCounts = useMemo(() => {
    const bucket = conversations[selectedSocial] ?? {
      active: [],
      archived: [],
      inTrash: [],
    };
    return {
      active: bucket.active.filter(filterDateConversation).length,
      archived: bucket.archived.filter(filterDateConversation).length,
      inTrash: bucket.inTrash.filter(filterDateConversation).length,
    };
  }, [conversations, selectedSocial, filterDateConversation]);

  const statusTabs: {
    id: ConversationStatus;
    labelId: string;
    count: number;
  }[] = [
    { id: "active", labelId: "app.chat.active", count: statusCounts.active },
    {
      id: "archived",
      labelId: "app.chat.archived",
      count: statusCounts.archived,
    },
    { id: "inTrash", labelId: "app.chat.inTrash", count: statusCounts.inTrash },
  ];

  const tabs: { id: WebChatListFilter; labelId: string }[] = [
    { id: "unresolved", labelId: "app.chat.inboxTab.unresolved" },
    { id: "unread", labelId: "app.chat.inboxTab.unread" },
    { id: "all", labelId: "app.chat.inboxTab.all" },
  ];

  return (
    <div className="webchat-inbox-toolbar">
      <div className="webchat-inbox-toolbar__channels" role="toolbar" aria-label={intl.formatMessage({ id: "app.chat.filterModal.sectionChannel" })}>
        {visibleChannels.map((channel) => {
          const isSelected = selectedSocial === channel.id;
          const label = intl.formatMessage({ id: channel.labelId });
          return (
            <FilterOption
              key={channel.id}
              enabled
              label=""
              title={label}
              onClick={() => setSelectedSocial(channel.id)}
              selected={isSelected}
              style={{ "--channel-brand": channel.brand } as React.CSSProperties}
              className={cn("webchat-inbox-toolbar__channel", {
                "webchat-inbox-toolbar__channel--selected": isSelected,
                "webchat-inbox-toolbar__channel--available": !isSelected,
              })}
              icon={
                <FontAwesomeIcon
                  className="webchat-inbox-toolbar__channel-icon"
                  icon={channel.icon}
                  aria-hidden
                />
              }
            />
          );
        })}
        {hasMoreChannelsToConnect ? (
          <button
            type="button"
            className="webchat-inbox-toolbar__connect-channels"
            onClick={goToBinding}
            title={intl.formatMessage({ id: "app.chat.inboxConnectChannelsHint" })}
          >
            <FontAwesomeIcon icon={["fas", "plus"]} aria-hidden />
            <span>{intl.formatMessage({ id: "app.chat.inboxConnectChannels" })}</span>
          </button>
        ) : null}
      </div>

      <div className="webchat-inbox-toolbar__search-row">
        <InputWembii
          type="text"
          placeholder={intl.formatMessage({
            id: "app.chat.inboxSearchPlaceholder",
          })}
          rounded
          showShadow
          className="webchat-inbox-toolbar__search flex-grow-1"
          style={{ borderRadius: "12px" }}
          value={searchText}
          onChange={(v) => onSearchChange(v)}
          icon={
            <FontAwesomeIcon
              icon={["fas", "search"]}
              size="1x"
              color="grey"
              className="pointer"
            />
          }
        />
        <button
          type="button"
          className="webchat-inbox-toolbar__filter-btn btn btn-light border shadow-sm"
          onClick={onOpenAdvancedFilters}
          aria-label={intl.formatMessage({ id: "app.chat.inboxMoreFilters" })}
        >
          <FontAwesomeIcon icon={["fas", "sliders-h"]} className="text-primary" />
        </button>
      </div>

      <div
        className="webchat-inbox-toolbar__status-tabs"
        role="tablist"
        aria-label={intl.formatMessage({ id: "app.chat.filterModal.sectionStatus" })}
      >
        {statusTabs.map(({ id, labelId, count }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={conversationStatus === id}
            className={cn("webchat-inbox-toolbar__status-tab", {
              "webchat-inbox-toolbar__status-tab--active":
                conversationStatus === id,
            })}
            onClick={() => setConversationStatus(id)}
          >
            <span>{intl.formatMessage({ id: labelId })}</span>
            <span className="webchat-inbox-toolbar__status-tab-count">
              ({count})
            </span>
          </button>
        ))}
      </div>

      <div className="webchat-inbox-toolbar__tabs" role="tablist">
        {tabs.map(({ id, labelId }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={listFilter === id}
            className={cn("webchat-inbox-toolbar__tab", {
              "webchat-inbox-toolbar__tab--active": listFilter === id,
            })}
            onClick={() => onListFilterChange(id)}
          >
            <span className="webchat-inbox-toolbar__tab-label">
              {intl.formatMessage({ id: labelId })}
            </span>
            {id === "unread" && hasUnread && (
              <span className="webchat-inbox-toolbar__tab-dot" aria-hidden />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
